import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Navbar1 } from "@/components/ui/navbar-1";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { amIAdmin } from "@/lib/blog.functions";

import {
  getCrmOverview,
  listCrmUsers,
  listEmailLogs,
  listSuppressedEmails,
  listFeedback,
  listCommunityPostReports,
  updateCommunityPostReport,
  type CrmUser,
  type FeedbackRow,
} from "@/lib/admin-crm.functions";
import { deleteCommunityPost } from "@/lib/community-feed.functions";
import { ExternalLink, Trash2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/crm")({
  head: () => ({
    meta: [
      { title: "CRM e Moderação — Viny Club" },
      { name: "description", content: "Área restrita para gestão e moderação do Viny Club." },
      { property: "og:title", content: "CRM e Moderação — Viny Club" },
      { property: "og:description", content: "Área restrita para gestão e moderação do Viny Club." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminCrm,
});

function AdminCrm() {
  const isAdminFn = useServerFn(amIAdmin);
  const { data: isAdmin, isLoading: checking } = useQuery({
    queryKey: ["crm", "is-admin"],
    queryFn: () => isAdminFn(),
  });

  if (checking) {
    return (
      <Shell>
        <p className="pt-6 text-sm text-muted-foreground">Verificando permissões…</p>
      </Shell>
    );
  }
  if (!isAdmin) {
    return (
      <Shell>
        <p className="pt-6 text-sm text-muted-foreground">Acesso restrito a administradores.</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">CRM</h1>
        <p className="text-sm text-muted-foreground">
          Visão geral, usuários, engajamento e comunicações.
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="w-full flex-wrap justify-start gap-1 h-auto min-h-9 py-1">
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="users">Usuários</TabsTrigger>
          <TabsTrigger value="emails">Emails</TabsTrigger>
          <TabsTrigger value="suppressed">Supressos</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="reports">Denúncias</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="emails"><EmailsTab /></TabsContent>
        <TabsContent value="suppressed"><SuppressedTab /></TabsContent>
        <TabsContent value="feedback"><FeedbackTab /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>

      </Tabs>

    </Shell>
  );
}



function Shell({ children }: { children: React.ReactNode }) {

  return (
    <div className="min-h-screen bg-background">
      <Navbar1 />
      <div className="mx-auto max-w-7xl px-4 pt-24 pb-16 sm:px-6">{children}</div>
    </div>
  );
}

// ---------- OVERVIEW ----------

function OverviewTab() {
  const fn = useServerFn(getCrmOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["crm", "overview"],
    queryFn: () => fn(),
    refetchInterval: 30_000,
  });

  if (isLoading || !data) return <p className="text-sm text-muted-foreground">Carregando métricas…</p>;

  const maxSignup = Math.max(1, ...data.signupsByDay.map((d) => d.count));
  const maxActive = Math.max(1, ...data.activeByDay.map((d) => d.count));
  const maxEmail = Math.max(1, ...data.emailsByDay.map((d) => Math.max(d.sent, d.failed)));

  return (
    <div className="space-y-6">
      {/* Real-time card */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex items-center justify-between pt-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Online agora</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-primary" />
              </span>
              <span className="text-3xl font-semibold">{data.activeNow}</span>
              <span className="text-sm text-muted-foreground">usuários (últimos 5 min)</span>
            </div>
          </div>
          <div className="text-right text-xs text-muted-foreground">Atualiza a cada 30s</div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Total de usuários" value={data.totalUsers} />
        <Kpi label="Novos 24h" value={data.newUsers24h} sub={`${data.newUsers7d} em 7d`} />
        <Kpi label="Ativos 24h" value={data.active24h} sub={`${data.active7d} em 7d`} />
        <Kpi label="Retornando 7d" value={data.returning7d} sub="cadastro >7d & ativo 7d" />
        <Kpi label="Emails enviados 7d" value={data.emailsSent7d} sub={`${data.emailsSent30d} em 30d`} />
        <Kpi label="Falhas de email 7d" value={data.emailsFailed7d} tone={data.emailsFailed7d ? "warn" : "ok"} />
        <Kpi label="Supressos" value={data.emailsSuppressed} />
        <Kpi label="Eventos XP 7d" value={data.totalXpEvents7d} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Novos usuários (30d)" data={data.signupsByDay} max={maxSignup} color="hsl(var(--primary))" />
        <ChartCard title="Usuários ativos (30d)" data={data.activeByDay} max={maxActive} color="#10b981" />
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 text-sm font-medium">Emails (30d)</div>
          <div className="flex h-32 items-end gap-1">
            {data.emailsByDay.map((d) => (
              <div key={d.day} className="flex flex-1 flex-col items-center gap-0.5" title={`${d.day}: ${d.sent} enviados, ${d.failed} falhas`}>
                <div className="flex w-full flex-col justify-end" style={{ height: "100%" }}>
                  {d.failed > 0 && (
                    <div className="w-full rounded-t-sm bg-destructive/70" style={{ height: `${(d.failed / maxEmail) * 80}%` }} />
                  )}
                  <div className="w-full bg-primary" style={{ height: `${(d.sent / maxEmail) * 80}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-primary" /> Enviados</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-destructive/70" /> Falhas</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 text-sm font-medium">Templates mais enviados (7d)</div>
          <div className="space-y-2">
            {data.topTemplates7d.length === 0 && (
              <div className="text-sm text-muted-foreground">Nenhum envio nos últimos 7 dias.</div>
            )}
            {data.topTemplates7d.map((t) => {
              const max = data.topTemplates7d[0]?.count || 1;
              return (
                <div key={t.template_name}>
                  <div className="flex justify-between text-xs">
                    <span className="font-mono">{t.template_name}</span>
                    <span className="text-muted-foreground">{t.count}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-muted">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${(t.count / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: "ok" | "warn" }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-1 text-2xl font-semibold ${tone === "warn" ? "text-destructive" : ""}`}>
          {value.toLocaleString("pt-BR")}
        </div>
        {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, data, max, color }: { title: string; data: Array<{ day: string; count: number }>; max: number; color: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-3 text-sm font-medium">{title}</div>
        <div className="flex h-32 items-end gap-1">
          {data.map((d) => (
            <div key={d.day} className="flex-1" title={`${d.day}: ${d.count}`}>
              <div className="w-full rounded-t-sm" style={{ height: `${(d.count / max) * 100}%`, background: color, minHeight: d.count ? 2 : 0 }} />
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
          <span>{data[0]?.day}</span>
          <span>{data[data.length - 1]?.day}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- USERS ----------

function UsersTab() {
  const fn = useServerFn(listCrmUsers);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["crm", "users", search, filter, page],
    queryFn: () => fn({ data: { search, filter, limit, offset: page * limit } }),
  });

  const filters = [
    { v: "all", l: "Todos" },
    { v: "online", l: "Online agora" },
    { v: "active7d", l: "Ativos 7d" },
    { v: "new7d", l: "Novos 7d" },
    { v: "returning", l: "Retornando" },
    { v: "dormant", l: "Dormentes 30d+" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Buscar por username ou nome…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs"
        />
        <div className="flex flex-wrap gap-1">
          {filters.map((f) => (
            <Button
              key={f.v}
              size="sm"
              variant={filter === f.v ? "default" : "outline"}
              onClick={() => { setFilter(f.v); setPage(0); }}
            >
              {f.l}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Engajamento</TableHead>
                  <TableHead className="text-right">XP</TableHead>
                  <TableHead className="text-right">Emails</TableHead>
                  <TableHead>Cadastro</TableHead>
                  <TableHead>Último acesso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>
                )}
                {data?.users.map((u) => <UserRow key={u.id} u={u} />)}
                {data?.users.length === 0 && !isLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground">Nenhum usuário encontrado.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {data ? `${page * limit + 1}–${Math.min((page + 1) * limit, data.total)} de ${data.total}` : ""}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={!data || (page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}>Próximo</Button>
        </div>
      </div>
    </div>
  );
}

function UserRow({ u }: { u: CrmUser }) {
  const engagementMap: Record<CrmUser["engagement"], { label: string; className: string }> = {
    online: { label: "Online", className: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
    hot: { label: "Ativo (24h)", className: "bg-primary/15 text-primary border-primary/30" },
    warm: { label: "Ativo (7d)", className: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
    cold: { label: "Inativo (30d)", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
    dormant: { label: "Dormente", className: "bg-muted text-muted-foreground border-border" },
  };
  const e = engagementMap[u.engagement];
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          {u.avatar_url ? (
            <img loading="lazy" decoding="async" src={u.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{u.display_name || u.username}</div>
            <div className="truncate text-xs text-muted-foreground">@{u.username}</div>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs">
        <span className="truncate">{u.email ?? "—"}</span>
        {u.is_suppressed && <Badge variant="destructive" className="ml-2">supresso</Badge>}
      </TableCell>
      <TableCell><Badge variant="outline" className={e.className}>{e.label}</Badge></TableCell>
      <TableCell className="text-right font-mono text-xs">{u.xp.toLocaleString("pt-BR")}</TableCell>
      <TableCell className="text-right font-mono text-xs">{u.emails_sent}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{fmtRelative(u.last_active_at)}</TableCell>
    </TableRow>
  );
}

// ---------- EMAILS ----------

function EmailsTab() {
  const fn = useServerFn(listEmailLogs);
  const [template, setTemplate] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(0);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ["crm", "emails", template, status, search, days, page],
    queryFn: () => fn({ data: { template, status, search, days, limit, offset: page * limit } }),
  });

  const statuses = ["sent", "pending", "failed", "dlq", "suppressed", "bounced", "complained"];

  const stats = useMemo(() => {
    if (!data) return null;
    const s = { sent: 0, failed: 0, other: 0 };
    for (const r of data.rows) {
      if (r.status === "sent") s.sent++;
      else if (["failed", "dlq", "bounced", "complained"].includes(r.status)) s.failed++;
      else s.other++;
    }
    return s;
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Filtrar por email…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="max-w-xs" />
        <select value={template} onChange={(e) => { setTemplate(e.target.value); setPage(0); }} className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Todos os templates</option>
          {data?.templates.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }} className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Todos os status</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-1">
          {[1, 7, 30, 90].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => { setDays(d); setPage(0); }}>{d}d</Button>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="Enviados" value={stats.sent} tone="ok" />
          <Kpi label="Falhas" value={stats.failed} tone={stats.failed ? "warn" : "ok"} />
          <Kpi label="Outros" value={stats.other} />
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Destinatário</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Quando</TableHead>
                  <TableHead>Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>}
                {data?.rows.map((r, i) => (
                  <TableRow key={`${r.message_id ?? ""}-${i}`}>
                    <TableCell className="font-mono text-xs">{r.template_name}</TableCell>
                    <TableCell className="text-xs">{r.recipient_email}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-xs text-destructive" title={r.error_message ?? ""}>{r.error_message ?? "—"}</TableCell>
                  </TableRow>
                ))}
                {data?.rows.length === 0 && !isLoading && (
                  <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">Nenhum envio encontrado.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          {data ? `${page * limit + 1}–${Math.min((page + 1) * limit, data.total)} de ${data.total}` : ""}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</Button>
          <Button size="sm" variant="outline" disabled={!data || (page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}>Próximo</Button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    sent: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    pending: "bg-muted text-muted-foreground",
    failed: "bg-destructive/15 text-destructive border-destructive/30",
    dlq: "bg-destructive/15 text-destructive border-destructive/30",
    bounced: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    complained: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    suppressed: "bg-orange-500/15 text-orange-600 border-orange-500/30",
  };
  return <Badge variant="outline" className={map[status] ?? ""}>{status}</Badge>;
}

// ---------- SUPPRESSED ----------

function SuppressedTab() {
  const fn = useServerFn(listSuppressedEmails);
  const { data, isLoading } = useQuery({ queryKey: ["crm", "suppressed"], queryFn: () => fn() });

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Quando</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>}
              {data?.map((r) => (
                <TableRow key={r.email}>
                  <TableCell className="text-sm">{r.email}</TableCell>
                  <TableCell><Badge variant="outline">{r.reason}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                </TableRow>
              ))}
              {data?.length === 0 && !isLoading && (
                <TableRow><TableCell colSpan={3} className="text-center text-sm text-muted-foreground">Nenhum email supresso.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- FEEDBACK ----------

function FeedbackTab() {
  const fn = useServerFn(listFeedback);
  const { data, isLoading } = useQuery({ queryKey: ["crm", "feedback"], queryFn: () => fn() });

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Página</TableHead>
                <TableHead>Mensagem</TableHead>
                <TableHead>Quando</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>}
              {data?.map((r: FeedbackRow) => (
                <TableRow key={r.id}>
                  <TableCell className="text-sm font-medium">{r.name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{r.email ?? "—"}</TableCell>
                  <TableCell><Badge variant="outline">{r.category}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.page ?? "—"}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs" title={r.message ?? ""}>{r.message ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtRelative(r.created_at)}</TableCell>
                </TableRow>
              ))}
              {data?.length === 0 && !isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Nenhum feedback recebido.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportsTab() {
  const qc = useQueryClient();
  const listFn = useServerFn(listCommunityPostReports);
  const updateFn = useServerFn(updateCommunityPostReport);
  const deleteFn = useServerFn(deleteCommunityPost);
  const { data, isLoading } = useQuery({ queryKey: ["crm", "community-post-reports"], queryFn: () => listFn() });
  const updateMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => updateFn({ data: { id, status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["crm", "community-post-reports"] }),
    onError: (error: Error) => toast.error(error.message),
  });
  const deleteMut = useMutation({
    mutationFn: ({ postId }: { postId: string; reportId: string }) => deleteFn({ data: { id: postId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "community-post-reports"] });
      toast.success("Publicação removida");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const reasons: Record<string, string> = {
    spam: "Spam", harassment: "Assédio", hate: "Discurso de ódio", nudity: "Nudez", violence: "Violência", misinformation: "Informação falsa", other: "Outro",
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Publicação</TableHead><TableHead>Motivo</TableHead><TableHead>Denunciante</TableHead><TableHead>Status</TableHead><TableHead>Quando</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Carregando…</TableCell></TableRow>}
              {data?.map((report) => (
                <TableRow key={report.id}>
                  <TableCell className="max-w-sm">
                    <div className="line-clamp-2 text-sm">{report.post_body || (report.post_media.length ? "Publicação com mídia" : "Publicação removida")}</div>
                    <div className="mt-1 text-xs text-muted-foreground">por @{report.author_username}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{reasons[report.reason] ?? report.reason}</Badge>{report.details && <div className="mt-1 max-w-xs text-xs text-muted-foreground">{report.details}</div>}</TableCell>
                  <TableCell className="text-xs">@{report.reporter_username}</TableCell>
                  <TableCell><StatusBadge status={report.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtRelative(report.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      {report.author_username !== "post removido" && (
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8"><Link to="/explore" search={{ tab: "feed", post: report.post_id }} aria-label="Abrir publicação"><ExternalLink className="h-4 w-4" /></Link></Button>
                      )}
                      {report.status === "open" && <Button variant="outline" size="sm" onClick={() => updateMut.mutate({ id: report.id, status: "dismissed" })}>Ignorar</Button>}
                      {report.author_username !== "post removido" && <Button variant="destructive" size="icon" className="h-8 w-8" aria-label="Remover publicação" onClick={() => { if (confirm("Remover esta publicação?")) deleteMut.mutate({ postId: report.post_id, reportId: report.id }); }}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {data?.length === 0 && !isLoading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Nenhuma denúncia recebida.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- utils ----------

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("pt-BR");
}
function fmtRelative(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("pt-BR");
}
